import { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Upload, Button, Image, message, DatePicker, Col, Row, } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { UploadOutlined } from "@ant-design/icons";
import callApi from "../../constants/API/CallApi";
import { ShowToast, Severty } from "../../helper/toast";
import { uploadFileToS3 } from "../../helper/uploadImage";
import { getEvent, ListBelt, ListClasses, updateEvent, getBranches, } from "../../constants/API/API";
import useApi from "../../hooks/useApi";
import moment from "moment";

const { Option } = Select;

const allowedTypes = ["image/png", "image/jpeg", "image/webp", "application/pdf", "video/mp4",];

const EventUpdate = ({ editVisible, setEditVisible, selectedEventId, refresh, }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [description, setDescription] = useState("");
  const [classes, setClasses] = useState([]);
  const [belts, setBelts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  useApi({
    callback: getBranches,
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ data }) => {
      const activeBranches = (data || []).filter(b => b.isActive);
      setBranches(activeBranches);
    },
  });

  useApi({
    callback: ListClasses,
    payload: selectedBranch ? { branchId: selectedBranch } : null,
    dependencies: [selectedBranch],
    shouldCallApi: Boolean(selectedBranch),
    onSuccess: ({ list }) => setClasses(list || []),
    onError: () => setClasses([]),
  });

  useApi({
    callback: ListBelt,
    payload: selectedBranch ? { branchId: selectedBranch } : null,
    dependencies: [selectedBranch],
    shouldCallApi: Boolean(selectedBranch),
    onSuccess: ({ list }) => {
      setBelts(list || []);
    },
    onError: (err) => {
      console.log("Error fetching belts", err);
      setBelts([]);
    },
  });


  useEffect(() => {
    if (selectedEventId && editVisible) {
      callApi({
        callback: () => getEvent(selectedEventId),
        payload: selectedEventId,
        onSuccess: ({ data }) => {

          const branchId = data?.branchId?._id || data?.branchId;
          setSelectedBranch(branchId);

          const { gallery } = data;
          const formattedGallery = (gallery || []).map((url, idx) => ({
            uid: `gallery-${idx}`,
            name: `image-${idx}`,
            status: "done",
            url: url,
            thumbUrl: url,
          }));

          setGallery(formattedGallery);
          form.setFieldsValue({
            ...data,
            branchId: data?.branchId?._id || data?.branchId,
            media: data?.media,
            gallery: formattedGallery,
            dateTime: moment(data?.dateTime),
            eligibility: {
              class: data?.eligibility?.class?.map((cls) => cls?._id) || [],
              belt: data?.eligibility?.belt?.map((belt) => belt?._id) || [],
            },
          });
          setDescription(data?.description);
          setFileList([
            {
              uid: "1",
              name: "uploaded file",
              status: "done",
              url: data?.media,
            },
          ]);
        },
        onError: (err) => {
          ShowToast(err, Severty.ERROR);
        },
      });
    }
  }, [selectedEventId, editVisible, form]);

  const handleCancel = () => {
    setEditVisible(false);
    form.resetFields();
    setFileList([]);
    setDescription("");
  };

  const handleGalleryChange = ({ fileList }) => {
    setGallery(
      fileList
        .filter(
          (f) =>
            f.status === "done" &&
            (f.url || (f.response && f.response.Location))
        )
        .map((f) => f.url || (f.response && f.response.Location))
    );
  };

  const beforeUpload = (file) => {
    if (!allowedTypes.includes(file.type)) {
      message.error("Unsupported file type.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 100 * 1024 * 1024) {
      message.error("File must be smaller than 100MB");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleUpload = async (info) => {
    const { file } = info;
    setLoading(true);
    try {
      const data = await uploadFileToS3({ file, dirName: "event" });
      const uploaded = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: data.Location,
      };

      setFileList([uploaded]);
      form.setFieldsValue({ media: data.Location });
    } catch (err) {
      message.error("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setFileList([]);
    form.setFieldsValue({ src: null });
  };

  const onFinish = (values) => {
    const payload = {
      ...values,
      description,
      media: fileList.length > 0 ? fileList[0].url : null,
      gallery: gallery.map((g) => (typeof g === "string" ? g : g.url)),
    };

    if (!description?.trim()) {
      return message.error("Enter valid description.");
    } else {
      payload.description = description?.trimStart();
    }

    setLoading(true);
    callApi({
      callback: updateEvent,
      payload: [payload, selectedEventId],
      onSuccess: ({ message }) => {
        ShowToast("Event updated successfully", Severty.SUCCESS);
        refresh(Date.now());
        setEditVisible(false);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      open={editVisible}
      width={800}
      centered
      title="Update Event"
      className="tab_modal"
      okButtonProps={{ htmlType: "submit", form: "edit-event", loading }}
      onCancel={handleCancel}
      afterClose={() => form.resetFields()}
    >
      <Form id="edit-event" onFinish={onFinish} form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={24} md={12}>
            <Form.Item
              normalize={(val) => val?.trimStart()}
              name="title"
              label="Title"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter title" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="address"
              label="Venue Address"
              rules={[{ required: true }]}
              normalize={(val) => val?.trimStart()}
            >
              <Input placeholder="Enter Venue Address" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="dateTime"
              label="Date & Time"
              rules={[
                { required: true, message: "Please select date and time" },
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                placeholder="Select Date & Time"
                disabledDate={(current) =>
                  current && current < moment().endOf("day")
                }
              />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="capacity"
              label="Maximum Participants"
              rules={[
                {
                  required: true,
                  message: "Please enter max participants limit!",
                },
              ]}
            >
              <Input placeholder="Enter Maximum Participants" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select branch!" }]}
            >
              <Select placeholder="Select Branch" showSearch optionFilterProp="children" onChange={(branchId) => setSelectedBranch(branchId)}>
                {branches?.map((branch) => (
                  <Select.Option key={branch?._id} value={branch?._id}>
                    {branch?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} sm={24} md={12}>
            <Form.Item
              name={["eligibility", "class"]}
              label="Class (Optional)"
            // rules={[{ required: true, message: "Please select a class!" }]}
            >
              <Select mode="multiple" placeholder="Select Class" showSearch optionFilterProp="children">
                {classes?.map((cls) => (
                  <Select.Option key={cls?._id} value={cls?._id}>
                    {cls?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} sm={24} md={12}>
            <Form.Item
              name={["eligibility", "belt"]}
              label="Belt (Optional)"
            // rules={[{ required: true, message: "Please select a belt!" }]}
            >
              <Select mode="multiple" placeholder="Select Belt" showSearch optionFilterProp="children">
                {belts?.map((belt) => (
                  <Select.Option key={belt?._id} value={belt?._id}>
                    {belt?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item name="media" label="Upload Event Image">
              <Upload
                maxCount={1}
                beforeUpload={beforeUpload}
                fileList={fileList}
                customRequest={handleUpload}
                onRemove={handleRemove}
                listType="picture"
                showUploadList={{
                  showPreviewIcon: false,
                  showRemoveIcon: true,
                }}
              >
                {fileList.length < 1 && (
                  <Button icon={<UploadOutlined />}>Upload</Button>
                )}
              </Upload>

              {fileList.length > 0 && (
                <div className="mt-2">
                  <Image width={100} src={fileList[0].url} />
                </div>
              )}
            </Form.Item>

          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="gallery"
              label="Gallery (Multiple Images)"
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList}
            >
              <Upload
                listType="picture"
                multiple
                customRequest={async ({ file, onSuccess }) => {
                  try {
                    const result = await uploadFileToS3({
                      file,
                      dirName: "dojo/eventGallery",
                    });
                    file.url = result.Location;
                    file.thumbUrl = result.Location;
                    onSuccess(file, file);
                  } catch (err) {
                    message.error("Failed to upload image");
                  }
                }}
                beforeUpload={beforeUpload}
                onChange={handleGalleryChange} // <-- Add this
              >
                <Button icon={<UploadOutlined />}>Upload Gallery Images</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Description">
              <ReactQuill value={description} onChange={setDescription} />
            </Form.Item>
          </Col>
        </Row>

        {/* <Form.Item name="class" label="Class (Optional)">
          <Select placeholder="Select class" allowClear>
            {classList.map((cls) => (
              <Option key={cls._id} value={cls._id}>
                {cls.name}
              </Option>
            ))}
          </Select>
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

export default EventUpdate;
